import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { TranslationFilled, CheckmarkOutline } from '@weee-fe/kunlunicon';
import DropDownStatic from '@/components/common/EnkiDummyUI/DropDownStatic';
import { useLanguageSwitcher } from '@/hooks/useLanguageSwitcher';

/**
 * 通用 Portal 组件，用于将子元素渲染到 body
 */
const Portal: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  return mounted ? createPortal(children, document.body) : null;
};

interface LanguageSelectProps {
  className?: string;
  clickTrack?: () => void;
  chooseLanguageTrack?: (lang: string) => void;
  onBeforeSendTrack?: (params: ObjectType) => ObjectType;
}

/** 与 listbox 的 w-[114px] 一致；-translate-x-1/2 时 left 为水平中点 */
const LISTBOX_WIDTH_PX = 114;
/** 贴左时 listbox 左缘与视口左缘的最小间距 */
const LISTBOX_VIEWPORT_EDGE_PX = 4;

/**
 * 极致优化后的 LanguageSelect 组件
 */
const LanguageSelect: React.FC<LanguageSelectProps> = props => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // 使用逻辑切换器，直接透传 props
  const { languages, lang, currentLanguage, changeLanguage } = useLanguageSwitcher(props);

  // 1. 只有打开时才去监听滚动，且只有 1 个监听器
  useEffect(() => {
    if (showDropdown) {
      const handleClose = () => setShowDropdown(false);
      window.addEventListener('scroll', handleClose);
      return () => window.removeEventListener('scroll', handleClose);
    }
  }, [showDropdown]);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showDropdown && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const halfW = LISTBOX_WIDTH_PX / 2;
      const minLeft = halfW + LISTBOX_VIEWPORT_EDGE_PX;
      const anchorX = rect.left + rect.width / 2;
      const left = anchorX < minLeft ? minLeft : anchorX;
      setPosition({ top: rect.bottom + 8, left });
    }
    setShowDropdown(!showDropdown);
  };

  const handleSelect = (newLang: string) => {
    changeLanguage(newLang);
    setShowDropdown(false);
  };

  if (!currentLanguage) {
    return <span className="skeleton bg-surface-200-bg w-[82px] h-[11px] rounded-100"></span>;
  }

  return (
    <div
      className={classNames('relative inline-block', props.className)}
      ref={triggerRef}
      aria-haspopup="listbox"
      aria-expanded={showDropdown}>
      <DropDownStatic
        onClick={toggleDropdown}
        mainIcon={<TranslationFilled width={14} height={14} className="flex-none text-primary-1" />}
        dataTestId="wid-language-select-trigger"
        label={currentLanguage?.label}
        mainColorTwNotation={'text-primary-1 enki-body-3xs-medium'}
        wrapperClassName="-ml-1.5 relative flex-none flex items-center justify-center before:bg-transparent h-[19px] px-[6px] whitespace-nowrap hover:bg-surface-200-bg active:bg-surface-200-bg rounded-200 text-primary-1 enki-body-3xs-medium cursor-pointer"
        hideCarat={false}
      />

      <Portal>
        {showDropdown && position && (
          <>
            {/* 2. 透明遮罩层：点击它就关闭下拉框，利用 React 属性彻底省去事件监听 */}
            <div
              className="fixed inset-0 z-[9998] bg-transparent cursor-default"
              onClick={() => setShowDropdown(false)}
            />
            <div
              role="listbox"
              aria-label="Language selection"
              data-testid="wid-language-select-popup"
              className="fixed bg-surface-100-bg rounded-200 shadow-xl border border-surface-100-hairline overflow-hidden z-[9999] -translate-x-1/2 w-[114px]"
              style={{ top: position.top, left: position.left }}>
              {languages.map(item => (
                <div
                  key={item.key}
                  role="option"
                  aria-label={item.label}
                  aria-selected={lang === item.key}
                  onClick={() => handleSelect(item.key)}
                  className={classNames(
                    'flex items-center justify-between py-[10px] px-4 cursor-pointer enki-body-sm transition-colors hover:bg-surface-200-bg active:bg-surface-300-bg',
                    'border-b border-solid border-surface-100-hairline last:border-none',
                    lang === item.key ? 'text-primary-1' : 'text-surface-100-fg-default'
                  )}>
                  <span>{item.label}</span>
                  {lang === item.key && <CheckmarkOutline className="text-primary-1 text-[12px]" />}
                </div>
              ))}
            </div>
          </>
        )}
      </Portal>
    </div>
  );
};

export default LanguageSelect;
